import { InfoCircleOutlined } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useRequest } from 'ahooks';
import { Form, Input, Modal, Switch, Typography } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import PageTopBar from 'components/navigation/PageTopBar';
import { Button } from 'components/ui';
import dayjs from 'dayjs';
import { colors, radius } from 'design';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import { useUpdateLicenseCode } from 'hooks/useUpdateLicenseCode';
import { supportEmail } from 'locales/fr/translation';
import posthog from 'posthog-js';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { userActions } from 'store/users';
import {
  selectCurrentUser,
  selectCurrentUserSubscription,
  selectNonRenewingSubscriptionExpiryDate,
} from 'store/users/selectors';
import Platform from 'utils/Platform';

const { Title } = Typography;

const useStyles = createUseStyles({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.surface.page,
    flex: 1,
    height: '100%',
    alignItems: 'center',
  },
  container: {
    padding: 8,
    overflowY: 'scroll',
    flexGrow: 1,
    maxWidth: 800,
  },
  title: {
    color: colors.brand.accentStrong,
  },
  section: {
    backgroundColor: 'white',
    padding: '0px 8px',
    borderRadius: radius.lg,
  },
  line: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 8px',
    gap: 8,
    '&:not(:last-child)': {
      borderBottom: `1px solid ${colors.surface.page}`,
    },
  },
  lineDescription: {
    fontSize: '0.7em',
    color: colors.text.tertiary,
    display: 'block',
    marginTop: 8,
    lineHeight: '1.5em',

    '& .anticon': {
      marginRight: 4,
      color: colors.brand.accentStrong,
    },
  },
  lineDescriptionDanger: {
    composes: '$lineDescription',
    color: colors.dataViz.veryBad,
  },
  lineDescriptionWarning: {
    composes: '$lineDescription',
    color: colors.dataViz.average,
  },
  licenseCode: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  noLicenseOrSubscription: {
    color: colors.text.tertiary,
  },
  newLicenseForm: {
    width: '100%',
    padding: '8px 8px 16px',
    '& .ant-form-item-label': {
      padding: 0,
      '& label': {
        color: colors.text.tertiary,
      },
    },
  },
  newLicenseFormButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
  },
  supportParagraph: {
    marginTop: 32,
    textAlign: 'center',
    fontSize: '0.8em',
    color: colors.text.tertiary,
  },
});

export default function SettingsPage() {
  // Hooks
  const styles = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [api, context] = useNotification();
  const currentUser = useSelector(selectCurrentUser);
  const [isChangingLicense, setIsChangingLicense] = useState(false);
  const currentUserSubscription = useSelector(selectCurrentUserSubscription);
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const nonRenewingSubscriptionExpiryDate = useSelector(selectNonRenewingSubscriptionExpiryDate);

  useRequest(() => Purchases.getCustomerInfo(), {
    manual: !Platform.isNative(),
    onSuccess: data => {
      dispatch(userActions.setCurrentUserSubscription(data.customerInfo));
    },
  });

  const { run: restorePurchases } = useRequest(() => Purchases.restorePurchases(), {
    manual: true,
    onSuccess: data => {
      dispatch(userActions.setCurrentUserSubscription(data.customerInfo));
      api.success({
        message: t('settings.subscription.restorePurchasesSuccess'),
      });
    },
    onError: error => {
      api.error({
        message: t('settings.subscription.restorePurchasesError'),
      });
      console.error(error);
    },
  });

  const { isPeriodTrackingEnabled, setShouldAskForPeriod, loadingShouldAskForPeriod } = useShouldAskForPeriod();

  const { run: storeConsentsToEmailNotifications, loading: loadingEmailConsents } = useRequest(
    consentsToEmailNotifications => ApiUsersManager.updateConsentsToEmailNotifications(consentsToEmailNotifications),
    {
      manual: true,
      onSuccess: data => {
        dispatch(userActions.setEmailConsents(data.consents_to_email_notifications));
      },
    },
  );

  const { run: updateLicenseCode } = useUpdateLicenseCode({
    api,
    onSuccess: () => {
      setIsChangingLicense(false);
    },
    posthogSource: 'settings',
  });

  const activeSubscription = currentUserSubscription?.activeSubscriptions[0];
  const licenseCodeExpiryDate = dayjs(currentUser?.license_code_expiry_date);
  const hasLicenseExpired = licenseCodeExpiryDate.isBefore(dayjs());

  return (
    <div className={styles.pageContainer}>
      <PageTopBar title={t('menu.settings')} />
      {context}
      <div className={styles.container}>
        <Title level={4} className={styles.title}>
          {t('settings.chatsSection')}
        </Title>
        <div className={styles.section}>
          <div className={styles.line} data-testid="settings.email">
            <p>
              {t('settings.chats.emailNotifications')}
              {Capacitor.getPlatform() === 'web' && (
                <>
                  <br />
                  <span className={styles.lineDescription}>
                    <InfoCircleOutlined />
                    {t('settings.chats.emailNotificationsDescription')}
                  </span>
                </>
              )}
            </p>
            <Switch
              checked={currentUser?.consents_to_email_notifications}
              loading={loadingEmailConsents}
              onChange={storeConsentsToEmailNotifications}
            />
          </div>
        </div>
        <Title level={4} className={styles.title}>
          {t('menu.tracking')}
        </Title>
        <div className={styles.section}>
          <div className={styles.line} data-testid="settings.period">
            <p>{t('settings.periodQuestions')}</p>
            <Switch
              checked={isPeriodTrackingEnabled}
              onChange={setShouldAskForPeriod}
              loading={loadingShouldAskForPeriod}
            />
          </div>
        </div>
        <Title level={4} className={styles.title}>
          {t('settings.accountSection')}
        </Title>
        <div className={styles.section}>
          {!isChangingLicense ? (
            <div className={styles.line}>
              <p>
                {currentUser?.license_code ? (
                  <>
                    {t('settings.license.currentLicense')}:{' '}
                    <span className={styles.licenseCode}>{currentUser?.license_code}</span>
                  </>
                ) : (
                  <span className={styles.noLicenseOrSubscription}>{t('settings.license.noLicense')}</span>
                )}
                {licenseCodeExpiryDate.isValid() && (
                  <span className={hasLicenseExpired ? styles.lineDescriptionDanger : styles.lineDescriptionWarning}>
                    {hasLicenseExpired ? (
                      <span>{t('settings.license.expired', { date: licenseCodeExpiryDate.format('LL') })}</span>
                    ) : (
                      <span>{t('settings.license.expiresOn', { date: licenseCodeExpiryDate.format('LL') })}</span>
                    )}
                  </span>
                )}
              </p>
              <Button variant="link" style={{ paddingRight: 0 }} onClick={() => setIsChangingLicense(true)}>
                {t('settings.license.changeLicense')}
              </Button>
            </div>
          ) : (
            <div>
              <Form
                onFinish={updateLicenseCode}
                className={styles.newLicenseForm}
                initialValues={{ licenseCode: (currentUser?.license_code || '').toUpperCase() }}
              >
                <Form.Item name="licenseCode" label={`${t('settings.license.newLicense')} :`}>
                  <Input type="text" placeholder={t('settings.license.placeholder')} />
                </Form.Item>
                <div className={styles.newLicenseFormButtons}>
                  <Button onClick={() => setIsChangingLicense(false)}>{t('settings.cancel')}</Button>
                  <Button variant="primary" htmlType="submit">
                    {t('settings.license.saveLicense')}
                  </Button>
                </div>
              </Form>
            </div>
          )}
          <div className={styles.line}>
            <p>
              {activeSubscription ? (
                t('settings.subscription.subscriptionInProgress')
              ) : nonRenewingSubscriptionExpiryDate ? (
                t('settings.subscription.nonRenewingSubscription', {
                  date: nonRenewingSubscriptionExpiryDate.format('LL'),
                })
              ) : (
                <span className={styles.noLicenseOrSubscription}>{t('settings.subscription.noSubscription')}</span>
              )}
              <br />
              {Platform.isNative() && (
                <Button
                  variant="link"
                  style={{ padding: 0, fontSize: '0.9em', height: 'auto' }}
                  onClick={restorePurchases}
                >
                  {t('settings.subscription.restorePurchases')}
                </Button>
              )}
            </p>
            {Platform.isNative() && activeSubscription && !activeSubscription.startsWith('prod_') && (
              <Link
                to={
                  {
                    ios: 'https://apps.apple.com/account/subscriptions',
                    android: 'https://play.google.com/store/account/subscriptions',
                  }[Platform.getPlatform()] || ''
                }
                onClick={() => {
                  posthog.capture('settings.subscription.manage_clicked');
                }}
              >
                <Button variant="link" style={{ paddingRight: 0 }}>
                  {t('settings.subscription.manage')}
                </Button>
              </Link>
            )}
            {activeSubscription && activeSubscription.startsWith('prod_') && (
              // These are the very first stripe subscriptions, cancelling goes through email
              <Button variant="link" style={{ paddingRight: 0 }} onClick={() => setIsStripeModalOpen(true)}>
                {t('settings.subscription.manage')}
              </Button>
            )}
            <Modal
              open={isStripeModalOpen}
              onCancel={() => setIsStripeModalOpen(false)}
              onOk={() => setIsStripeModalOpen(false)}
              cancelButtonProps={undefined}
            >
              <p>
                <Trans
                  i18nKey="settings.subscription.manageStripeSubscription"
                  components={{ a: <Link to={`mailto:${supportEmail}`} />, br: <br /> }}
                />
              </p>
            </Modal>
          </div>
        </div>
        <p className={styles.supportParagraph}>
          <Trans
            i18nKey="settings.supportParagraph"
            components={{ a: <Link to={`mailto:${supportEmail}`} />, br: <br /> }}
          />
        </p>
      </div>
    </div>
  );
}
